import React, { useEffect, useState } from 'react';
import { X, GitBranch, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../../component-styles/RecordPages.css';
import API_BASE from '../../../utils/api.js';
import { documentLabel } from '../../../utils/documentTitle.js';

function getApiBase() {
  return API_BASE;
}

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function getRetainerApplicationsForDoc(retainer, docType, docId) {
  const normalizedDocId = String(docId || '');
  const matchedAppliedDocs = safeArray(retainer?.appliedDocs).filter(
    (doc) => doc?.docType === docType && String(doc?.docId || '') === normalizedDocId
  );

  const matchedHistory = safeArray(retainer?.paymentHistory).filter((entry) => {
    if (entry?.paymentType !== 'application') return false;

    const historyDocType = entry?.appliedTo?.docType || entry?.targetType || '';
    const historyDocId = entry?.appliedTo?.docId || entry?.targetId || '';
    return historyDocType === docType && String(historyDocId) === normalizedDocId;
  });

  return [...matchedAppliedDocs, ...matchedHistory];
}

function getListClass(items) {
  const count = Array.isArray(items) ? items.length : 0;
  return `folio-applied-list${count > 6 ? ' scrollable' : ''}`;
}

function getInvoiceSourceProposalId(invoice) {
  return (
    invoice?.proposalId ||
    invoice?.sourceProposalId ||
    invoice?.source?.proposalId ||
    invoice?.meta?.proposalId ||
    invoice?.source?.proposal?.proposalId ||
    ''
  );
}

export default function InvoiceFolioModal({ invoice, projectId, onClose }) {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [credits, setCredits] = useState([]);
  const [retainers, setRetainers] = useState([]);
  const [originProposalId, setOriginProposalId] = useState('');
  const [originProposalNumber, setOriginProposalNumber] = useState('');

  useEffect(() => {
    if (!invoice?.invoiceId || !userId || !projectId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    const API_BASE = getApiBase();

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [poRes, creditsRes, retRes, proposalRes] = await Promise.all([
          fetch(
            `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/${encodeURIComponent(
              projectId
            )}`,
            { cache: 'no-store' }
          ),
          fetch(`${API_BASE}/api/credits/${encodeURIComponent(projectId)}`, {
            headers: { userid: userId },
          }),
          fetch(`${API_BASE}/api/retainers/${encodeURIComponent(projectId)}`, {
            headers: { userid: userId },
          }),
          fetch(
            `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
              projectId
            )}`,
            { cache: 'no-store' }
          ),
        ]);

        const poJson = poRes.ok ? await poRes.json() : [];
        const creditsJson = creditsRes.ok ? await creditsRes.json() : [];
        const retJson = retRes.ok ? await retRes.json() : [];
        const proposalsJson = proposalRes.ok ? await proposalRes.json() : [];

        const invoiceId = String(invoice.invoiceId);
        const invoiceProposalId = String(getInvoiceSourceProposalId(invoice));
        const resolvedProposalId = (() => {
          if (invoiceProposalId) return invoiceProposalId;

          const directMatch = safeArray(proposalsJson).find((proposal) => {
            if (String(proposal?.convertedInvoiceId || '') === invoiceId) return true;
            const convertedIds = Array.isArray(proposal?.convertedInvoiceIds)
              ? proposal.convertedInvoiceIds
              : [];
            return convertedIds.map(String).includes(invoiceId);
          });

          return String(directMatch?.proposalId || '');
        })();

        const originProposal = safeArray(proposalsJson).find(
          (p) => String(p?.proposalId || '') === resolvedProposalId
        );

        const matchedPOs = safeArray(poJson).filter((po) => {
          const sourceId =
            po.invoiceId || po.sourceInvoiceId || po.source?.invoiceId || po.meta?.invoiceId || '';
          return String(sourceId) === invoiceId;
        });

        const appliedCredits = safeArray(creditsJson)
          .map((credit) => {
            const applied = safeArray(credit.appliedDocs).filter(
              (doc) => doc.docType === 'invoice' && String(doc.docId) === invoiceId
            );
            if (applied.length === 0) return null;
            const totalApplied = applied.reduce((s, d) => s + Number(d.amount || 0), 0);
            return {
              creditId: credit.creditId,
              creditNumber: credit.creditNumber || credit.creditId,
              totalApplied,
            };
          })
          .filter(Boolean);

        const appliedRetainers = safeArray(retJson)
          .map((ret) => {
            const applied = getRetainerApplicationsForDoc(ret, 'invoice', invoiceId);
            if (applied.length === 0) return null;
            const totalApplied = applied.reduce((s, record) => s + Number(record.amount || 0), 0);
            return {
              retainerId: ret.retainerId,
              retainerNumber: ret.retainerNumber || ret.retainerId,
              totalApplied,
            };
          })
          .filter(Boolean);

        if (!mounted) return;
        setOriginProposalId(resolvedProposalId);
        setOriginProposalNumber(originProposal?.proposalNumber || '');
        setPurchaseOrders(matchedPOs);
        setCredits(appliedCredits);
        setRetainers(appliedRetainers);
      } catch (err) {
        if (mounted) setError(err.message || 'Failed to load invoice folio.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [invoice, projectId, userId]);

  const total = formatCurrency(invoice?.totals?.total ?? invoice?.totals?.grandTotal);
  const paid = formatCurrency(invoice?.totals?.paid || 0);

  if (!invoice) return null;

  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {documentLabel(invoice.invoiceNumber, invoice.title) || 'Invoice'}
          <span className="folio-sub">{invoice.client?.name || '-'}</span>
        </h3>

        <div className="folio-summary">
          <div>
            <label>Total</label>
            <div>{total}</div>
          </div>
          <div>
            <label>Paid</label>
            <div>{paid}</div>
          </div>
        </div>

        {loading && <div className="folio-empty">Loading folio...</div>}
        {!loading && error && <div className="folio-empty">{error}</div>}

        {!loading && !error && (
          <>
            {originProposalId && (
              <>
                <h4 className="folio-section-label">Origin Proposal</h4>
                <div className="folio-applied-list">
                  <Link
                    to={`/projects/${projectId}/proposals/${originProposalId}`}
                    className="folio-applied-row"
                  >
                    <GitBranch size={16} className="folio-row-icon" />
                    <div className="folio-row-text">
                      <b>{originProposalNumber || originProposalId}</b> - Proposal
                    </div>
                    <ExternalLink size={14} className="folio-open-icon" />
                  </Link>
                </div>
              </>
            )}

            <h4 className="folio-section-label">
              Purchase Orders Created ({purchaseOrders.length})
            </h4>
            <div className={getListClass(purchaseOrders)}>
              {purchaseOrders.length === 0 && (
                <div className="folio-empty">No purchase orders linked yet.</div>
              )}
              {purchaseOrders.map((po) => (
                <Link
                  to={`/projects/${projectId}/purchase-orders/${po.poId}`}
                  key={po.poId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{po.orderNumber || po.poId}</b> - Purchase Order
                    <span style={{ marginLeft: 6 }}>{formatCurrency(po.total)}</span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>

            <h4 className="folio-section-label">Credits Applied ({credits.length})</h4>
            <div className={getListClass(credits)}>
              {credits.length === 0 && <div className="folio-empty">No credits applied.</div>}
              {credits.map((c) => (
                <Link
                  to={`/projects/${projectId}/credits`}
                  key={c.creditId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{c.creditNumber}</b> - Credit
                    <span style={{ marginLeft: 6 }}>{formatCurrency(c.totalApplied)}</span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>

            <h4 className="folio-section-label">Retainer Applications ({retainers.length})</h4>
            <div className={getListClass(retainers)}>
              {retainers.length === 0 && (
                <div className="folio-empty">No retainers applied to this invoice.</div>
              )}
              {retainers.map((r) => (
                <Link
                  to={`/projects/${projectId}/retainers/${r.retainerId}`}
                  key={r.retainerId}
                  className="folio-applied-row"
                >
                  <GitBranch size={16} className="folio-row-icon" />
                  <div className="folio-row-text">
                    <b>{r.retainerNumber}</b> - Retainer
                    <span style={{ marginLeft: 6 }}>{formatCurrency(r.totalApplied)}</span>
                  </div>
                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
